import { ee } from '../../services/earth-engine';

/**
 * Breaking an area of interest into pieces small enough for Earth Engine to answer in
 * one go.
 * Anything that walks an area pixel by pixel - sampling it, training a model on it, predicting over
 * it - stops answering past a certain size and comes back as a timeout rather than as a result. The
 * area that is too large is not an error the user made: it is the normal size of an estuary. So the
 * work is cut into stripes and done one at a time.
 * Kept apart from the page that first needed it (the CSV export) because the models that train on
 * the area need exactly the same thing, and a second copy of this would be a second set of limits
 * to keep in step.
 */

/**
 * @constant SAFE_PIXEL_LIMIT
 * @description Pixel count above which a single pass over the area risks exceeding Earth Engine's
 * in-browser computation and memory limits. Matches the ~150k/part heuristic used in the reference
 * GEE pipeline (Mascagni, 2026).
 * @memberof module:Bathymetry
 */
export const SAFE_PIXEL_LIMIT = 150000;

/**
 * @function estimateRegionPixelCount
 * @description Cheaply estimates how many pixels a pass over the given region at the given scale
 * would touch, from the region's geodesic area divided by the pixel area. Cheap on purpose: asking
 * Earth Engine to actually count them is the very operation that is at risk of timing out.
 * @param {Object} regionGeom - The region, as an `ee.Geometry`.
 * @param {Number} scale - Pixel scale, in metres.
 * **Called by:
 * @see {planRegionParts}
 * @memberof module:Bathymetry
 * @returns {Promise<Number>} The estimated pixel count.
 */
export const estimateRegionPixelCount = (regionGeom, scale) => {
  const pixelCount = ee.Number(regionGeom.area(1)).divide(scale * scale);
  return new Promise((resolve, reject) => {
    pixelCount.evaluate((result, error) => (error ? reject(error) : resolve(result)));
  });
};

/**
 * @function splitRegionIntoParts
 * @description Splits a region into non-overlapping stripes along the longest axis of its bounding
 * box, each intersected back with the original region - the same approach as the reference GEE
 * pipeline's `splitROIIntoN`. Striping rather than a grid keeps every part a single piece, which
 * matters because each one is handed to Earth Engine on its own.
 * @param {Object} regionGeom - The region, as an `ee.Geometry`.
 * @param {String} crsStr - CRS used to measure the bounding box axes.
 * @param {Number} nParts - How many stripes to cut.
 * **Called by:
 * @see {planRegionParts}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.List` of `nParts` geometries.
 */
export const splitRegionIntoParts = (regionGeom, crsStr, nParts) => {
  const proj = ee.Projection(crsStr);
  const bounds = regionGeom.bounds(1, proj);
  const ring = ee.List(bounds.coordinates().get(0));
  const xs = ring.map((pt) => ee.Number(ee.List(pt).get(0)));
  const ys = ring.map((pt) => ee.Number(ee.List(pt).get(1)));
  const xmin = ee.Number(xs.reduce(ee.Reducer.min()));
  const xmax = ee.Number(xs.reduce(ee.Reducer.max()));
  const ymin = ee.Number(ys.reduce(ee.Reducer.min()));
  const ymax = ee.Number(ys.reduce(ee.Reducer.max()));
  const width = xmax.subtract(xmin);
  const height = ymax.subtract(ymin);
  const splitAlongX = width.gte(height);

  return ee.List.sequence(0, nParts - 1).map((i) => {
    i = ee.Number(i);
    return ee.Geometry(
      ee.Algorithms.If(
        splitAlongX,
        (() => {
          const dx = width.divide(nParts);
          const x0 = xmin.add(dx.multiply(i));
          const x1 = xmin.add(dx.multiply(i.add(1)));
          return ee.Geometry.Rectangle([x0, ymin, x1, ymax], proj, false).intersection(regionGeom, 1);
        })(),
        (() => {
          const dy = height.divide(nParts);
          const y0 = ymin.add(dy.multiply(i));
          const y1 = ymin.add(dy.multiply(i.add(1)));
          return ee.Geometry.Rectangle([xmin, y0, xmax, y1], proj, false).intersection(regionGeom, 1);
        })(),
      ),
    );
  });
};

/**
 * @function partsForPixelCount
 * @description How many stripes an area of this many pixels has to be cut into. Never one part over
 * the limit and never fewer than two once the limit is passed - a single stripe would be the whole
 * area again.
 * @param {Number} estimatedPixels - As {@link estimateRegionPixelCount} reports it.
 * @param {Number} [limit] - Pixels per part.
 * **Called by:
 * @see {planRegionParts}
 * @memberof module:Bathymetry
 * @returns {Number} The number of parts; 1 when the area fits in one pass.
 */
export const partsForPixelCount = (estimatedPixels, limit = SAFE_PIXEL_LIMIT) => {
  if (!Number.isFinite(estimatedPixels) || estimatedPixels <= limit) return 1;
  return Math.max(2, Math.ceil(estimatedPixels / limit));
};

/**
 * @function planRegionParts
 * @description Works out how to cover an area without asking Earth Engine for more than it will
 * answer: how big the area is, how many passes that takes, and the geometry of each pass.
 * An area that fits comes back as a single part - the caller runs the same loop either way instead
 * of branching on size.
 * A failed estimate is not a failed run: it comes back as one part, so the work is attempted whole.
 * Guessing wrong there costs a timeout the user can retry; refusing to start costs them the
 * analysis.
 * @param {Object} options
 * @param {Object} options.region - The area, as an `ee.Geometry`.
 * @param {Number} options.scale - Pixel scale, in metres.
 * @param {String} options.crs - CRS used to measure the area when it has to be cut.
 * @param {Number} [options.limit] - Pixels per part.
 * **Called by:
 * @see {exportPixelsToCSV} - sampling every pixel of the area.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{estimatedPixels, parts, geometries, limit}` - `geometries` is an
 *  `ee.List` when the area was cut, and null when it was not.
 */
export const planRegionParts = async ({ region, scale, crs, limit = SAFE_PIXEL_LIMIT }) => {
  let estimatedPixels = null;
  try {
    estimatedPixels = await estimateRegionPixelCount(region, scale);
  } catch (error) {
    console.error('[tiling] the area could not be measured; running it in one pass:', error);
  }

  const parts = partsForPixelCount(estimatedPixels, limit);
  return {
    estimatedPixels,
    parts,
    limit,
    geometries: parts > 1 ? splitRegionIntoParts(region, crs, parts) : null,
  };
};
